import * as THREE from 'three';
import { CHUNK_SIZE, RENDER_DISTANCE, WORLD_HEIGHT, WORLD_LIMIT } from '../constants.js';
import { BlockId, isPassable, isLightSource } from './BlockType.js';
import { Chunk } from './Chunk.js';
import { ChunkMesher } from './ChunkMesher.js';
import { calculateChunkLight } from './LightEngine.js';

const CHUNKS_Y = Math.floor(WORLD_HEIGHT / CHUNK_SIZE);

// Half render distance threshold for LOD (chunks beyond this get reduced detail)
const LOD_DISTANCE_SQ = Math.floor(RENDER_DISTANCE / 2) * Math.floor(RENDER_DISTANCE / 2);

// Reusable frustum and matrix for culling
const _frustum = new THREE.Frustum();
const _projScreenMatrix = new THREE.Matrix4();

export class World {
  constructor(scene, terrainGenerator, atlasTexture, saveManager) {
    this.chunks = new Map();
    this.scene = scene;
    this.generator = terrainGenerator;
    this.mesher = new ChunkMesher(this, atlasTexture);
    this.saveManager = saveManager;

    // Camera reference for frustum culling (set via setCamera)
    this._camera = null;
    this._frustumNeedsUpdate = true;

    // Player chunk position cache to avoid redundant work
    this._lastPcx = null;
    this._lastPcz = null;

    // Reusable set for needed chunk keys
    this._neededKeys = new Set();

    // Dirty chunks sorted by distance for priority meshing
    this._dirtyQueue = [];

    // Farming system reference (set externally) for crop visual stages
    this.farmingSystem = null;
  }

  /**
   * Set the camera reference for frustum culling.
   * Call from main.js after camera creation: world.setCamera(camera)
   */
  setCamera(camera) {
    this._camera = camera;
  }

  /** Switch the terrain generator (for dimension travel). */
  setGenerator(gen) {
    this.generator = gen;
  }

  /** Remove all loaded chunks from the scene and clear the map. */
  clearAllChunks() {
    for (const [, chunk] of this.chunks) {
      if (chunk.mesh) this.scene.remove(chunk.mesh);
      if (chunk.transparentMesh) this.scene.remove(chunk.transparentMesh);
      chunk.dispose();
    }
    this.chunks.clear();
    this._lastPcx = null;
    this._lastPcz = null;
    this._dirtyQueue.length = 0;
  }

  chunkKey(cx, cy, cz) {
    return `${cx},${cy},${cz}`;
  }

  getChunk(cx, cy, cz) {
    return this.chunks.get(this.chunkKey(cx, cy, cz)) || null;
  }

  getLoadedChunkCount() {
    return this.chunks.size;
  }

  getBlock(wx, wy, wz) {
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cy = Math.floor(wy / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const chunk = this.getChunk(cx, cy, cz);
    if (!chunk) return BlockId.AIR;
    const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const ly = ((wy % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const lz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    return chunk.getBlock(lx, ly, lz);
  }

  setBlock(wx, wy, wz, blockId) {
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cy = Math.floor(wy / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const chunk = this.getChunk(cx, cy, cz);
    if (!chunk) return;
    const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const ly = ((wy % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const lz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    chunk.setBlock(lx, ly, lz, blockId);

    // Record modification for persistence
    if (this.saveManager) {
      this.saveManager.recordBlockChange(wx, wy, wz, blockId);
    }

    // Dirty neighboring chunks if on boundary
    if (lx === 0) this._dirtyNeighbor(cx - 1, cy, cz);
    if (lx === CHUNK_SIZE - 1) this._dirtyNeighbor(cx + 1, cy, cz);
    if (ly === 0) this._dirtyNeighbor(cx, cy - 1, cz);
    if (ly === CHUNK_SIZE - 1) this._dirtyNeighbor(cx, cy + 1, cz);
    if (lz === 0) this._dirtyNeighbor(cx, cy, cz - 1);
    if (lz === CHUNK_SIZE - 1) this._dirtyNeighbor(cx, cy, cz + 1);

    // When a light-affecting block changes, dirty neighboring chunks
    // so their light maps get recalculated
    if (isLightSource(blockId) > 0 || blockId === BlockId.AIR) {
      this._dirtyLightNeighbors(cx, cy, cz);
    }
  }

  /**
   * Mark all 26 neighboring chunks as dirty for light recalculation.
   * Only marks chunks that actually exist (are loaded).
   */
  _dirtyLightNeighbors(cx, cy, cz) {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          if (dx === 0 && dy === 0 && dz === 0) continue;
          this._dirtyNeighbor(cx + dx, cy + dy, cz + dz);
        }
      }
    }
  }

  _dirtyNeighbor(cx, cy, cz) {
    const chunk = this.getChunk(cx, cy, cz);
    if (chunk) chunk.isDirty = true;
  }

  /** Mark the chunk containing world position (wx, wy, wz) as dirty for re-meshing */
  markChunkDirtyAt(wx, wy, wz) {
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cy = Math.floor(wy / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const chunk = this.getChunk(cx, cy, cz);
    if (chunk) chunk.isDirty = true;
  }

  isSolid(wx, wy, wz) {
    const block = this.getBlock(wx, wy, wz);
    return !isPassable(block);
  }

  isWater(wx, wy, wz) {
    return this.getBlock(wx, wy, wz) === BlockId.WATER;
  }

  isLava(wx, wy, wz) {
    return this.getBlock(wx, wy, wz) === BlockId.LAVA;
  }

  isPortal(wx, wy, wz) {
    return this.getBlock(wx, wy, wz) === BlockId.NETHER_PORTAL;
  }

  /**
   * Update the frustum from the camera. Call once per frame before rendering.
   * If no camera is set, frustum culling is skipped (all chunks visible).
   */
  updateFrustum() {
    if (!this._camera) return;
    _projScreenMatrix.multiplyMatrices(
      this._camera.projectionMatrix,
      this._camera.matrixWorldInverse
    );
    _frustum.setFromProjectionMatrix(_projScreenMatrix);
    this._frustumNeedsUpdate = false;

    // Update visibility for all loaded chunks
    for (const [, chunk] of this.chunks) {
      this._updateChunkVisibility(chunk);
    }
  }

  /**
   * Check if a chunk's bounding box intersects the camera frustum
   * and set mesh visibility accordingly.
   */
  _updateChunkVisibility(chunk) {
    if (!this._camera) return;

    const isVisible = _frustum.intersectsBox(chunk.boundingBox);

    if (chunk.mesh) {
      chunk.mesh.visible = isVisible;
    }
    if (chunk.transparentMesh) {
      chunk.transparentMesh.visible = isVisible;
    }
  }

  updateLoadedChunks(playerPos) {
    const pcx = Math.floor(playerPos.x / CHUNK_SIZE);
    const pcz = Math.floor(playerPos.z / CHUNK_SIZE);

    const needed = this._neededKeys;
    needed.clear();

    const minChunkX = Math.floor(-WORLD_LIMIT / CHUNK_SIZE);
    const maxChunkX = Math.floor(WORLD_LIMIT / CHUNK_SIZE);
    const minChunkZ = Math.floor(-WORLD_LIMIT / CHUNK_SIZE);
    const maxChunkZ = Math.floor(WORLD_LIMIT / CHUNK_SIZE);

    for (let dx = -RENDER_DISTANCE; dx <= RENDER_DISTANCE; dx++) {
      for (let dz = -RENDER_DISTANCE; dz <= RENDER_DISTANCE; dz++) {
        if (dx * dx + dz * dz > RENDER_DISTANCE * RENDER_DISTANCE) continue;
        const cx = pcx + dx;
        const cz = pcz + dz;
        if (cx < minChunkX || cx > maxChunkX || cz < minChunkZ || cz > maxChunkZ) continue;

        for (let cy = 0; cy < CHUNKS_Y; cy++) {
          const key = this.chunkKey(cx, cy, cz);
          needed.add(key);

          if (!this.chunks.has(key)) {
            const chunk = new Chunk(cx, cy, cz);
            this.generator.fillChunk(chunk);

            // Apply saved modifications
            if (this.saveManager) {
              this.saveManager.applyToChunk(chunk, CHUNK_SIZE);
            }

            this.chunks.set(key, chunk);
          }
        }
      }
    }

    // Unload distant chunks
    for (const [key, chunk] of this.chunks) {
      if (!needed.has(key)) {
        if (chunk.mesh) this.scene.remove(chunk.mesh);
        if (chunk.transparentMesh) this.scene.remove(chunk.transparentMesh);
        chunk.dispose();
        this.chunks.delete(key);
      }
    }

    // Collect dirty non-empty chunks and sort by distance (closer = higher priority)
    this._dirtyQueue.length = 0;
    for (const [, chunk] of this.chunks) {
      if (chunk.isDirty && !chunk.isEmpty()) {
        // Calculate squared distance from player chunk
        const dx = chunk.cx - pcx;
        const dz = chunk.cz - pcz;
        const distSq = dx * dx + dz * dz;
        this._dirtyQueue.push({ chunk, distSq });
      } else if (chunk.isDirty && chunk.isEmpty()) {
        // Empty chunk: remove any old mesh and mark clean
        if (chunk.mesh) {
          this.scene.remove(chunk.mesh);
          chunk.mesh.geometry.dispose();
          chunk.mesh = null;
        }
        if (chunk.transparentMesh) {
          this.scene.remove(chunk.transparentMesh);
          chunk.transparentMesh.geometry.dispose();
          chunk.transparentMesh = null;
        }
        chunk.isDirty = false;
      }
    }

    // Sort: closer chunks get meshed first
    this._dirtyQueue.sort((a, b) => a.distSq - b.distSq);

    // Re-mesh dirty chunks with budget (prioritize closer chunks)
    let meshBudget = 4;
    for (let i = 0; i < this._dirtyQueue.length && meshBudget > 0; i++) {
      const { chunk, distSq } = this._dirtyQueue[i];
      // Calculate light map before building mesh
      calculateChunkLight(chunk, this);
      // Pass LOD flag: chunks beyond half render distance use reduced detail
      const isLOD = distSq > LOD_DISTANCE_SQ;
      this.mesher.buildMesh(chunk, isLOD);
      meshBudget--;
    }
  }

  getTopBlock(wx, wz) {
    for (let wy = WORLD_HEIGHT - 1; wy >= 0; wy--) {
      if (this.isSolid(wx, wy, wz)) {
        return wy;
      }
    }
    return 0;
  }
}
